import React, { useRef, useEffect } from 'react';
import { Image as ImageIcon, Palette, Upload, RefreshCw, X } from 'lucide-react';
import './BgReplaceControls.css';

const BgReplaceControls = ({ settings, onChange }) => {
    // Defaults
    const mode = settings.mode || 'color'; 
    const color = settings.color || '#ffffff';
    
    // We separate the Preview (URL) from the File (Binary)
    // 'bgImage' will hold the actual File object for the API
    // 'bgPreview' will hold the visible URL for the UI
    const bgPreview = settings.bgPreview || null;

    const fileInputRef = useRef(null);

    // Helper to update parent state
    const update = (key, value) => {
        onChange({ ...settings, [key]: value });
    };

    // --- 1. HANDLE FILE UPLOAD CORRECTLY ---
    const handleBgUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Create a temporary URL for the UI
            const previewUrl = URL.createObjectURL(file);
            
            // Update settings:
            // 1. 'bgImage': The FILE OBJECT (Sent to API)
            // 2. 'bgPreview': The BLOB URL (Shown to User)
            // 3. 'mode': Switch to image mode
            onChange({
                ...settings,
                bgImage: file,       
                bgPreview: previewUrl, 
                mode: 'image'
            });
        }
    };

    // Cleanup memory when component unmounts
    useEffect(() => {
        return () => {
            if (bgPreview && bgPreview.startsWith('blob:')) {
                URL.revokeObjectURL(bgPreview);
            }
        };
    }, [bgPreview]);

    const clearBgImage = (e) => {
        e.stopPropagation();
        onChange({
            ...settings,
            bgImage: null,
            bgPreview: null
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const PRESETS = ['#FFFFFF', '#F3F4F6', '#1F2937', '#8A181A', '#5F4729'];

    return (
        <div className="bg-replace-controls fade-in">
            
            {/* --- MODE TOGGLE --- */}
            <div className="mode-toggle-group">
                <button 
                    className={`mode-btn ${mode === 'color' ? 'active' : ''}`}
                    onClick={() => update('mode', 'color')}
                >
                    <Palette size={16} />
                    <span>Color</span>
                </button>
                <button 
                    className={`mode-btn ${mode === 'image' ? 'active' : ''}`}
                    onClick={() => update('mode', 'image')}
                >
                    <ImageIcon size={16} />
                    <span>Image</span>
                </button>
            </div>

            {/* --- OPTION A: COLOR PICKER --- */}
            {mode === 'color' && (
                <div className="control-section fade-in">
                    <label className="section-label">Select Color</label>
                    
                    <div className="hex-input-wrapper">
                        <div className="color-swatch-container">
                            <input 
                                type="color" 
                                value={color}
                                onChange={(e) => update('color', e.target.value)}
                                className="native-color-picker"
                            />
                        </div>
                        <input 
                            type="text" 
                            value={color}
                            onChange={(e) => update('color', e.target.value)}
                            className="hex-input-field"
                            placeholder="#FFFFFF"
                            maxLength={7}
                        />
                    </div>

                    <div className="presets-grid">
                        {PRESETS.map((preset) => (
                            <button
                                key={preset}
                                className={`preset-circle ${color === preset ? 'selected' : ''}`}
                                style={{ backgroundColor: preset }}
                                onClick={() => update('color', preset)}
                                title={preset}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* --- OPTION B: CUSTOM IMAGE UPLOAD (Fixed) --- */}
            {mode === 'image' && (
                <div className="control-section fade-in">
                    <label className="section-label">Custom Background</label>
                    
                    {/* HIDDEN INPUT */}
                    <input 
                        type="file" 
                        ref={fileInputRef}
                        onChange={handleBgUpload}
                        accept="image/*"
                        hidden
                    />

                    {bgPreview ? (
                        /* --- STATE 1: IMAGE SELECTED (Compact Preview) --- */
                        <div className="bg-image-selected-card">
                            <img src={bgPreview} alt="Selected BG" className="bg-thumbnail-small" />
                            
                            <div className="bg-info-col">
                                <span className="bg-status-text">Image Selected</span>
                                <button 
                                    className="btn-text-change"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Change Image
                                </button>
                            </div>

                            <button onClick={clearBgImage} className="btn-icon-remove" title="Remove">
                                <X size={16} />
                            </button>
                        </div>
                    ) : (
                        /* --- STATE 2: EMPTY (Click to Upload) --- */
                        <div 
                            className="custom-bg-uploader"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="upload-placeholder">
                                <div className="icon-circle-small"><Upload size={18} /></div>
                                <span>Click to Upload</span>
                                <small>JPG, PNG, WebP</small>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BgReplaceControls;